import { useEffect, useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { ToggleGroup, ToggleGroupItem } from "../../ui/toggle-group";
import { cn } from "../../utils/common/cn";
import {
  UI_DEFAULT_VALUE_BASIS,
  VALUE_BASIS_LABEL,
  type ValueBasis,
} from "../../utils/portfolio-risk-concentration/api/concentration";
import { useInvestorScope } from "../../utils/portfolio-risk-concentration/api/facilities";
import {
  useInvestors,
  type Investor as InvestorRecord,
} from "../../utils/portfolio-risk-concentration/api/capital";

/**
 * The one filter vocabulary for the whole product, and the one widget that renders it.
 *
 * Before this, the same Internal/External question was asked three different ways: a Radix
 * Select on Monitor and Snapshot, a ToggleGroup duplicated into both Manage limits tabs, and a
 * third ToggleGroup on Capacity that overrode the shared selected-state colour because the
 * default one collided with its hover state. Uppercase values in one place, lowercase in
 * another; URL state in one, local useState in another. Two pages could disagree about which
 * book you were looking at and neither was wrong.
 *
 * So: one component, one set of values, one style — see FILTER_TRIGGER for the size and
 * why it is what it is.
 *
 * ── Scope is a toggle, not a dropdown ────────────────────────────────────────────────────
 * Internal and External are the top-level split of the whole product and there are exactly
 * two of them, so both are rendered side by side and the current one is visible without
 * opening anything. A two-option Select hid half the product behind a click and gave no hint
 * that the other half existed. The rest of the bar stays on Selects — those have three or
 * more options and one honest default.
 *
 * ── Facility is the book, and there is only one such dimension ───────────────────────────
 * "CAP 1 / CAP 2" and "facility" are the same question. `facility_code` is CAP1/CAP2/INTERNAL
 * and its display name is the book name ('CAP 1 Back Book'). There is deliberately NOT a
 * separate Facility filter alongside a Book filter — that would be two controls over one
 * column. `unique_to_facility` ('No' / 'Unique to CAP 1' / …) is a different column and a
 * different question, and is not exposed here.
 *
 * Every control stays visible and clickable in every combination. Facility was at various
 * points hidden on Internal and then greyed out on it; both stranded a choice the user had
 * made and both made the bar reflow on a scope click. The book is now sent to the API on
 * every scope — inert on the internal path until prc_limit_config carries a book dimension,
 * and applied automatically once it does.
 */
export type Scope = "EXTERNAL" | "INTERNAL";

export const SCOPES: { value: Scope; label: string }[] = [
  { value: "EXTERNAL", label: "External" },
  { value: "INTERNAL", label: "Internal" },
];

export const SCOPE_LABEL: Record<Scope, string> = {
  EXTERNAL: "External",
  INTERNAL: "Internal",
};

/**
 * Longer form, for prose and alert copy.
 *
 * No "(CAP 1 & CAP 2)" after "External books". It was there when External always meant both
 * books, and the Facility filter made it a lie: with CAP 1 selected the Monitor banner still
 * announced both. The book in force is named by the Facility control directly above the
 * banner and by the Book column in the table, so restating it here could only ever
 * contradict them.
 */
export const SCOPE_DESCRIPTION: Record<Scope, string> = {
  EXTERNAL: "External books",
  INTERNAL: "Internal risk appetite",
};

/** Which book an external row belongs to. Internal rows never show this column. Used by the Scenario pages; the other pages read the
 *  facilities the database lists (useBookLabels() in api/facilities.ts). */
export const BOOK_LABEL: Record<string, string> = {
  CAP1: "CAP 1",
  CAP2: "CAP 2",
  INTERNAL: "Internal",
};

/**
 * ALL means "both books in one table", which is what External alone used to mean.
 *
 * There is deliberately NO "Equity" entry here. Equity is a funding source, not a book: the
 * facilities ARE the debt (Wafra funds through CAP 1 and CAP 2), and equity is Navitas'.
 * The Scope toggle already separates the two views that matter — External is the limits
 * Wafra gives us on their books, Internal is the limits we set against whichever capital
 * backs that investor — so a fourth facility value would be a third way to ask a question
 * two controls already answer.
 */
// A facility code: "ALL", or a facility that has risk limits (CAP1, CAP2, ... as the database lists them).
export type FacilityFilter = string;

/**
 * The Facility dropdown's options for the chosen investor: "All Facilities", then every facility that investor has (Wafra: CAP 1 and CAP
 * 2; the ABS Scenario Portfolio: ABS SCENARIO). "All investors" lists every one. Read from the database (`GET /investor-scope`), the way
 * Scenario Portfolio reads an investor code's facilities, so a facility added in Manage Capital shows up under its investor with no code
 * change. A facility that has no risk limits is listed too, and says so when it is picked.
 *
 * `onlyWithLimits` keeps just the facilities that have risk limits: for what only a limit can use (an alert, a new limit). No facilities,
 * not a guess, while the list loads.
 */
export function useFacilityOptions(investor: Investor, onlyWithLimits = false): { value: FacilityFilter; label: string }[] {
  const scope = useInvestorScope(investor);
  return useMemo(
    () => [
      { value: "ALL", label: "All Facilities" },
      ...scope.facilities.filter((f) => !onlyWithLimits || f.hasLimits).map((f) => ({ value: f.code, label: f.name })),
    ],
    [scope.facilities, onlyWithLimits],
  );
}

/**
 * The Scope toggle's options for an investor: External when it has facilities, Internal when it has internal limits
 * switched on -- both of them while that is loading, and when it has neither (the page then says there is nothing, rather than
 * offering nothing). Wafra has no Internal until the client sets internal limits up.
 */
export function useScopeOptions(investor: Investor): { value: Scope; label: string }[] {
  const scope = useInvestorScope(investor);
  return useMemo(() => {
    if (scope.isLoading) return SCOPES;
    const offered = SCOPES.filter((s) => (s.value === "EXTERNAL" ? scope.hasFacilities : scope.hasInternal));
    return offered.length ? offered : SCOPES;
  }, [scope.isLoading, scope.hasFacilities, scope.hasInternal]);
}

/**
 * ── Loans/Leases is its own dimension, INDEPENDENT of Facility ───────────────────────────
 * Facility asks which funding vehicle; this asks what kind of contract. Two questions, two
 * controls, two pieces of state, two URL params (`?book=` and `?product=`).
 *
 * They used to share one. That was defensible — CAP 1 is the loans book and CAP 2 is the
 * leases book, and `bankdwn_daily.contract_type` holds the literal 'CAP 1' / 'CAP 2' and
 * nothing else, verified against the live data — so the two selects were one column asked
 * twice, and binding them removed any way for them to disagree. The cost was that picking
 * "Leases" visibly moved Facility to "CAP 2 Back Book": a control nobody touched
 * changing on its own, which reads as a bug however well-founded it is.
 *
 * So they are separate now, and the API INTERSECTS them. "CAP 1 + Leases" is a real
 * question with the honest answer "nothing", returned with a note saying why, rather than
 * a contradiction the UI silently prevents. And the day the warehouse carries a genuine
 * loan/lease flag — leases funded through CAP 1 — this starts narrowing on its own with no
 * change here: only the mapping in concentration.py::_PRODUCT_BOOKS moves.
 */
export type ProductFilter = "ALL" | "LOAN" | "LEASE";

export const PRODUCT_FILTERS: { value: ProductFilter; label: string }[] = [
  { value: "ALL", label: "Loans & Leases" },
  { value: "LOAN", label: "Loans" },
  { value: "LEASE", label: "Leases" },
];

/** Reads `?product=`. Anything unrecognised widens to ALL rather than narrowing wrongly. */
export function productFromParam(value: string | null | undefined): ProductFilter {
  const v = (value ?? "").toUpperCase();
  return v === "LOAN" || v === "LEASE" ? v : "ALL";
}

/**
 * ── Investor ─────────────────────────────────────────────────────────────────────────────
 * Recorded against the LIMIT (`prc_limit_config.investor`, sql/73_), not against the
 * exposure. There is still no investor column in the warehouse: every row PRC reports on is
 * filtered to `consider = true AND label_owned_serviced = 'Owned'`, so both investors'
 * limits are measured over the same population. What the switch changes is WHOSE APPETITE
 * is being shown — which limits exist and what thresholds they carry.
 *
 * CAP 1 and CAP 2 are Wafra facilities, so an investor without an external facility has no
 * external limits. That is expressed by the API returning an EMPTY list with a note, NOT by
 * this component hiding the External scope: a product whose controls appear and disappear as
 * you change one dropdown is harder to trust than one that answers "nothing here, and here is
 * why". The emptiness is a fact about the data and is decided server-side
 * (app/utils/investors.py's get_external_investors()), so the day another investor gains an
 * external facility it shows up with no change here.
 */
// A plain string, not a closed union: the roster is DB-backed (prc_investor) now, so a third
// investor is a real value this type must accept. Every consumer already does
// `INVESTOR_LABEL[code] ?? code` defensively, so widening from the old "ALL"|"WAFRA"|"NAVITAS"
// union costs nothing at existing call sites.
export type Investor = string;

/**
 * The static WAFRA/NAVITAS pair — used only as the loading/error fallback before
 * useInvestorFilterOptions()'s live roster arrives, and by anything not yet on that hook.
 * Prefer useInvestorFilterOptions() in a component; these are the two the product has always
 * had, not a source of truth for what exists today.
 */
export const INVESTORS: { value: Investor; label: string }[] = [
  { value: "WAFRA", label: "Wafra" },
  { value: "NAVITAS", label: "Navitas" },
];

/** Static fallback with "All investors" in front — see INVESTORS and useInvestorFilterOptions(). */
export const INVESTORS_WITH_ALL: { value: Investor; label: string }[] = [
  { value: "ALL", label: "All investors" },
  ...INVESTORS,
];

/** Static fallback label map — see INVESTORS and useInvestorFilterOptions()'s labelOf(). */
export const INVESTOR_LABEL: Record<string, string> = {
  ALL: "All investors",
  WAFRA: "Wafra",
  NAVITAS: "Navitas",
};

const FALLBACK_INVESTOR_RECORDS: InvestorRecord[] = [
  { investorCode: "WAFRA", displayName: "Wafra", isExternalFunder: true },
  { investorCode: "NAVITAS", displayName: "Navitas", isExternalFunder: false },
];

/**
 * The live investor roster for a filter bar — Monitor, Capacity, Snapshot, Alerts. Falls back
 * to FALLBACK_INVESTOR_RECORDS while useInvestors() is loading or on error, so first paint is
 * unchanged and there is no spinner in the filter bar: same pattern
 * AddCapitalSourceDialog.tsx/ManageCapitalPage.tsx already use for this same query.
 */
export function useInvestorFilterOptions() {
  const q = useInvestors();
  const records = q.data ?? FALLBACK_INVESTOR_RECORDS;
  const investors = useMemo(
    () => records.map((r) => ({ value: r.investorCode, label: r.displayName })),
    [records],
  );
  const investorsWithAll = useMemo(
    () => [{ value: "ALL", label: "All investors" }, ...investors],
    [investors],
  );
  const labelOf = (code: string) =>
    records.find((r) => r.investorCode === code)?.displayName ?? code;
  // "All" spans the investor who does, so the external side is never empty under it.
  const isExternalFunder = (code: string) =>
    code === "ALL" || records.some((r) => r.investorCode === code && r.isExternalFunder);
  return { investors, investorsWithAll, labelOf, isExternalFunder, isLoading: q.isLoading };
}

/** Reads `?investor=`, defaulting to Wafra so every existing link resolves unchanged. */
export function investorFromParam(value: string | null | undefined): Investor {
  const v = (value ?? "").toUpperCase();
  if (!v || v === "WAFRA") return "WAFRA";
  return v;
}

/** Reads `?facility=`, tolerating the CAP1/CAP2 values older links still carry. */
export function scopeFromParam(value: string | null | undefined): Scope {
  const v = (value ?? "").toUpperCase();
  if (v === "INTERNAL") return "INTERNAL";
  // CAP1/CAP2/EXTERNAL/absent all land on External — a bookmarked ?facility=CAP2
  // still shows that book's rows, now alongside CAP 1's.
  return "EXTERNAL";
}

/**
 * Reads `?book=`. A bookmarked `?facility=CAP2` from before the two params split carries the
 * book too, so it is honoured here as well — otherwise scopeFromParam would widen such a link
 * to both books, which is not what it asked for.
 */
export function facilityFromParam(
  book: string | null | undefined,
  legacyFacility?: string | null,
): FacilityFilter {
  const v = (book ?? "").toUpperCase();
  if (v === "ALL") return "ALL";
  if (v) return v;
  const legacy = (legacyFacility ?? "").toUpperCase();
  // an older link's ?facility=<a book> (anything but the two scopes) is the book it named
  if (legacy && legacy !== "EXTERNAL" && legacy !== "INTERNAL") return legacy;
  return "ALL";
}

/**
 * The value `/concentration?facility=` wants. Note the two vocabularies in the API:
 * /concentration takes uppercase EXTERNAL|INTERNAL|CAP1|CAP2, /limits takes lowercase
 * internal|external. This returns the former; callers of /limits use `facilityTypeParam`.
 */
export function concentrationFacilityParam(scope: Scope, facility: FacilityFilter): string {
  if (scope === "INTERNAL") return "INTERNAL";
  return facility === "ALL" ? "EXTERNAL" : facility;
}

/** The value `/limits?facility=` wants. Books are not addressable there. */
export function facilityTypeParam(scope: Scope): "internal" | "external" {
  return scope === "INTERNAL" ? "internal" : "external";
}

/** True when the row belongs to the selected book. `ALL` keeps every external row. */
export function matchesFacility(facilityCode: string, facility: FacilityFilter): boolean {
  return facility === "ALL" || facilityCode === facility;
}

/**
 * True when the row is the selected product.
 *
 * Loans are CAP 1 and leases are CAP 2 — the same mapping the API applies in
 * concentration.py::_PRODUCT_BOOKS. Restated here for the ONE surface that filters client
 * side (Manage limits reads /limits, which lists configuration rather than snapshots and
 * takes no product param), so the two cannot silently disagree about what "Leases" means.
 *
 * Every other page pushes the selection into the request instead.
 */
export function matchesProduct(facilityCode: string, product: ProductFilter): boolean {
  if (product === "ALL") return true;
  return facilityCode === (product === "LOAN" ? "CAP1" : "CAP2");
}

/**
 * The whole filter state, read from and written to the URL, identically on every page.
 *
 * This exists because "every page has the same filters" was true of the RENDERING long
 * before it was true of the STATE. Each page separately called scopeFromParam and
 * facilityFromParam, and each separately decided what a scope change did to the other
 * params — Monitor and Capacity both deleted `?book=` on a scope change, Manage limits
 * deleted it too, so walking between pages could silently widen a CAP 2 selection to both
 * books. One hook, one set of rules.
 *
 * The rules, in full:
 *
 *   * A scope change writes ONLY the scope. The book selection and the investor survive it,
 *     so switching Internal -> External -> Internal returns you to exactly where you were.
 *   * An investor change writes ONLY the investor. It does NOT force the scope, even for an
 *     investor with no external facility — that combination is legal and returns an empty
 *     table with a note. Nothing here second-guesses the data.
 *   * The book selection is kept and stays live in every scope. It is passed to the API on
 *     the internal path too, where it is currently inert (prc_limit_config has no
 *     contract_type column) but is applied the moment one exists — see
 *     limit_rows.fetch_internal_facts. Nothing is discarded and no control is disabled.
 */
/**
 * The measurement basis control's options.
 *
 * Deliberately spelled out rather than "NI / PV": these words appear as a COLUMN HEADING
 * over dollar figures, and an abbreviation there is how someone ends up reading present
 * value as net investment.
 */
export const VALUE_BASIS_FILTERS: { value: ValueBasis; label: string }[] = [
  { value: "net_investment", label: VALUE_BASIS_LABEL.net_investment },
  { value: "present_value", label: VALUE_BASIS_LABEL.present_value },
  { value: "fair_market_value", label: VALUE_BASIS_LABEL.fair_market_value },
];

/**
 * `?valueBasis=` -> a basis, defaulting to present value (what the dropdowns start on).
 *
 * Anything unrecognised falls back to that default rather than throwing, and the figures are
 * always fetched and labelled for the basis that is returned here, so a heading never names a
 * different basis than the dollars under it.
 */
// Checked against VALUE_BASIS_FILTERS's own values rather than a second hardcoded list, so a
// future basis added there (like fair_market_value was) can't silently fall through here again.
const _VALID_VALUE_BASES = new Set<string>(VALUE_BASIS_FILTERS.map((o) => o.value));

export function valueBasisFromParam(raw: string | null): ValueBasis {
  return raw && _VALID_VALUE_BASES.has(raw) ? (raw as ValueBasis) : UI_DEFAULT_VALUE_BASIS;
}

export interface PrcFilters {
  scope: Scope;
  facility: FacilityFilter;
  product: ProductFilter;
  investor: Investor;
  /** Which quantity the figures are measured in. See VALUE_BASIS_FILTERS. */
  valueBasis: ValueBasis;
  facilityType: "internal" | "external";
  isExternal: boolean;
  /** The value /concentration and /capacity want for `?facility=`. */
  concentrationFacility: string;
  setScope: (next: Scope) => void;
  setFacility: (next: FacilityFilter) => void;
  setProduct: (next: ProductFilter) => void;
  setInvestor: (next: Investor) => void;
  setValueBasis: (next: ValueBasis) => void;
}

export function usePrcFilters(
  searchParams: URLSearchParams,
  setSearchParams: (
    next: (prev: URLSearchParams) => URLSearchParams,
    opts?: { replace?: boolean },
  ) => void,
  /** Monitor and Capacity close their detail sheet when the row set changes. */
  onChange?: () => void,
): PrcFilters {
  const investor = investorFromParam(searchParams.get("investor"));
  // What the chosen investor actually has (from the database): a link or an earlier choice that names a scope or a facility it does not
  // have reads as the one it does, so no page asks for -- or shows -- a combination that cannot exist.
  const has = useInvestorScope(investor);
  const urlScope = scopeFromParam(searchParams.get("facility"));
  const scope: Scope =
    has.isLoading ? urlScope
    : urlScope === "INTERNAL" && !has.hasInternal && has.hasFacilities ? "EXTERNAL"
    : urlScope === "EXTERNAL" && !has.hasFacilities && has.hasInternal ? "INTERNAL"
    : urlScope;
  const urlFacility = facilityFromParam(searchParams.get("book"), searchParams.get("facility"));
  const facility: FacilityFilter =
    has.isLoading || urlFacility === "ALL" || has.facilities.some((f) => f.code === urlFacility) ? urlFacility : "ALL";
  const product = productFromParam(searchParams.get("product"));
  const valueBasis = valueBasisFromParam(searchParams.get("valueBasis"));

  const write = (mutate: (next: URLSearchParams) => void) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      mutate(next);
      return next;
    });
    onChange?.();
  };

  return {
    scope,
    facility,
    product,
    investor,
    valueBasis,
    facilityType: facilityTypeParam(scope),
    isExternal: scope === "EXTERNAL",
    concentrationFacility: concentrationFacilityParam(scope, facility),
    // Note what is NOT here: no `next.delete("book")`. That deletion is what used to make a
    // scope toggle destroy the book selection.
    setScope: (v) => write((next) => next.set("facility", v)),
    setFacility: (v) => write((next) => next.set("book", v)),
    // Writes only `product`. Nothing reconciles it against the book — that is the whole
    // point of splitting them, and the API answers the intersection.
    setProduct: (v) => write((next) => next.set("product", v)),
    // Writes only the investor. Switching to one with no external facility while the scope
    // is External leaves you on External, looking at an empty table that says why — which
    // is what was asked for, and is also what a bookmarked link has always to resolve to.
    setInvestor: (v) => write((next) => next.set("investor", v)),
    // Deleted rather than written when it is the default (present value), so the URL of an
    // ordinary view stays clean and shareable. A net investment or FMV view carries
    // ?valueBasis= explicitly.
    setValueBasis: (v) =>
      write((next) =>
        v === UI_DEFAULT_VALUE_BASIS ? next.delete("valueBasis") : next.set("valueBasis", v),
      ),
  };
}

export interface PrcFilterBarProps {
  scope: Scope;
  onScopeChange: (next: Scope) => void;
  facility?: FacilityFilter;
  onFacilityChange?: (next: FacilityFilter) => void;
  product?: ProductFilter;
  onProductChange?: (next: ProductFilter) => void;
  investor?: Investor;
  onInvestorChange?: (next: Investor) => void;
  /** Defaults to the two real investors. Reading surfaces pass INVESTORS_WITH_ALL. */
  investorOptions?: { value: Investor; label: string }[];
  /** Rendered immediately after the Scope toggle, as another control in the row. */
  children?: React.ReactNode;
  /**
   * Pushed to the FAR right of the same row. The as-of date picker goes here on every page
   * that has one — it used to sit in a different place on each (its own row under the bar
   * on Monitor, beside the methodology caption on Capacity, next to the "Showing…" line on
   * Snapshot), so the eye had to hunt for it when moving between pages.
   */
  right?: React.ReactNode;
  className?: string;
}

/**
 * One size for every control in the bar, exported so a page adding its own control
 * (Monitor's Basis) matches it instead of hardcoding a second number.
 *
 * 220px x 4 selects plus the scope toggle ran to roughly 1,130px — most of a laptop
 * viewport spent on filters, above every table in the product. 160px still fits
 * "All facilities" and "Loans & leases" whole; the two long book names ellipsis in the
 * trigger and read in full in the open list, which is the right trade: the trigger is a
 * reminder of the current choice, the list is where the choice is made.
 */
export const FILTER_TRIGGER = "h-9 w-[160px]";

/**
 * For Facility alone. Its options are book NAMES — "CAP 1 Back Book" — and at 160px
 * they ellipsis to "CAP2 Lease Back Bo", which is unreadable and, worse, cuts off exactly
 * the word that distinguishes the two books. Every other control in the bar fits its
 * longest option whole, so this is a one-control exception rather than a new size.
 */
export const FILTER_TRIGGER_WIDE = "h-9 w-[230px]";

/**
 * A labelled dropdown in the product's one filter style.
 *
 * Exported because Capacity needs this LOOK without being a filter: its control picks a
 * headroom calculation method, not a subset of rows. Before this existed it borrowed
 * `PrcFilterBar` with a relabelled scope select and a `showInvestor={false}` escape hatch,
 * which made a calculation lens visually identical to the Internal/External scope filter —
 * and it got read as one.
 *
 * So: share the styling, not the semantics.
 */
export function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-0.5">
      <span className="block text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
        {label}
      </span>
      {children}
    </div>
  );
}

export function FilterSelect<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled = false,
  wide = false,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange?: (next: T) => void;
  disabled?: boolean;
  /** Facility only — see FILTER_TRIGGER_WIDE. */
  wide?: boolean;
}) {
  return (
    <FilterField label={label}>
      <Select
        value={value}
        disabled={disabled}
        onValueChange={(v) => onChange?.(v as T)}
      >
        <SelectTrigger
          className={cn(wide ? FILTER_TRIGGER_WIDE : FILTER_TRIGGER, "truncate")}
          aria-label={label}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FilterField>
  );
}

/**
 * Internal / External, both visible at once.
 *
 * The selected-state colour is set explicitly rather than taken from the shared `outline`
 * toggle variant: that variant gives `hover` and `data-[state=on]` the same `bg-accent`, so
 * the selected option and the one merely under the cursor look identical. Capacity's old
 * ToggleGroup had to override exactly this, and the override was lost when it was replaced by
 * a Select — hence the note at the top of this file. Do not "simplify" this back to the
 * variant default.
 *
 * `type="single"` with a Radix ToggleGroup can deselect to `""`, which would leave the
 * product in no scope at all, so an empty value is ignored rather than propagated.
 */
export function ScopeToggle({
  scope,
  onScopeChange,
  investor = "ALL",
}: {
  scope: Scope;
  onScopeChange: (next: Scope) => void;
  /** Whose scopes to offer (see useScopeOptions); every scope for "All investors". */
  investor?: Investor;
}) {
  const options = useScopeOptions(investor);
  return (
    <FilterField label="Scope">
      <ToggleGroup
        type="single"
        value={scope}
        onValueChange={(v) => {
          if (v) onScopeChange(v as Scope);
        }}
        className="h-9 justify-start gap-0 rounded-md border border-input bg-background p-0.5"
        aria-label="Scope"
      >
        {options.map((s) => (
          <ToggleGroupItem
            key={s.value}
            value={s.value}
            aria-label={s.label}
            className={cn(
              "h-8 min-w-[74px] rounded-[3px] px-3 text-sm font-medium",
              "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
              "data-[state=on]:bg-primary data-[state=on]:text-primary-foreground",
              "data-[state=on]:hover:bg-primary data-[state=on]:hover:text-primary-foreground",
            )}
          >
            {s.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </FilterField>
  );
}

export function PrcFilterBar({
  scope,
  onScopeChange,
  facility,
  onFacilityChange,
  product = "ALL",
  onProductChange,
  investor = "WAFRA",
  onInvestorChange,
  investorOptions = INVESTORS,
  children,
  right,
  className,
}: PrcFilterBarProps) {
  // ── Every control is live in every combination ──────────────────────────────────────────
  // Nothing here is hidden or disabled by the selection. Facility and Loans/Leases were both
  // at different times: first hidden on Internal, then greyed out on it. Both were wrong for
  // the same reason — a control that changes state as you move around implies the product has
  // changed shape, and it strands the choice the user already made. They stay clickable, the
  // book is sent to the API on every scope, and it narrows internal limits as soon as the
  // data carries a book dimension (limit_rows.fetch_internal_facts).
  //
  // Likewise both Scope options are always offered. An investor with no external facility
  // gets an empty external table and a note — the API's answer, not a missing button.
  const hasBookControls = facility !== undefined && onFacilityChange !== undefined;
  // The facilities the chosen investor has. A facility picked under another investor is not one of them: back to All.
  const facilityOptions = useFacilityOptions(investor);
  const facilitiesLoaded = !useInvestorScope(investor).isLoading;
  useEffect(() => {
    if (!hasBookControls || !facilitiesLoaded || facility === "ALL") return;
    if (!facilityOptions.some((o) => o.value === facility)) onFacilityChange!("ALL");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityOptions, facilitiesLoaded, facility, hasBookControls]);

  return (
    <div className={cn("flex flex-wrap items-end gap-2", className)}>
      <FilterSelect
        label="Investor"
        value={investor}
        options={investorOptions}
        onChange={onInvestorChange}
        disabled={!onInvestorChange}
      />

      {hasBookControls && (
        <>
          <FilterSelect
            label="Facility"
            value={facility!}
            // a value that is not one of the investor's facilities (left over from another investor, until the reset above lands) still
            // reads as itself instead of leaving the trigger blank
            options={
              facilityOptions.some((o) => o.value === facility)
                ? facilityOptions
                : [...facilityOptions, { value: facility!, label: facility! }]
            }
            onChange={onFacilityChange}
            wide
          />
          {/* Its own state and its own param — see PRODUCT_FILTERS. */}
          <FilterSelect
            label="Loans / Leases"
            value={product}
            options={PRODUCT_FILTERS}
            onChange={onProductChange}
          />
        </>
      )}

      <ScopeToggle scope={scope} onScopeChange={onScopeChange} investor={investor} />

      {children}

      {/* ml-auto rather than justify-between on the container: with flex-wrap,
          justify-between spreads the CONTROLS apart on a narrow viewport instead of just
          pushing this to the end. */}
      {right && <div className="ml-auto flex items-end">{right}</div>}
    </div>
  );
}


